import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function PublicInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/public/invoice/${id}`)
      .then((r) => setInv(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0E1A14] text-[#C5A059] px-6 text-center" data-testid="public-invoice-error">
        Invoice not found.
      </div>
    );

  if (!inv)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0E1A14] text-[#C5A059]/70" data-testid="public-invoice-loading">
        Loading invoice…
      </div>
    );

  const dateTime = [inv.bill_date, inv.bill_time].filter(Boolean).join("  ");

  return (
    <div className="min-h-screen bg-[#0E1A14] py-8 px-4 flex justify-center" data-testid="public-invoice-page">
      <div className="w-full max-w-2xl bg-white text-[#15241C] rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#15241C] text-white px-8 py-7 text-center">
          <h1 className="font-serif text-3xl tracking-wide text-[#C5A059]" data-testid="public-invoice-salon">DEFINE SALONS</h1>
          {inv.branch_name && <p className="text-sm mt-2 text-white/90">{inv.branch_name}</p>}
          {inv.branch_address && <p className="text-xs mt-1 text-white/70">{inv.branch_address}</p>}
          {inv.branch_phone && <p className="text-xs mt-1 text-white/70">Phone: {inv.branch_phone}</p>}
          <p className="text-xs mt-1 font-medium text-[#C5A059]" data-testid="public-invoice-gst">GST No.: {inv.gst_no}</p>
        </div>

        {/* Meta */}
        <div className="px-8 py-5 grid grid-cols-2 gap-3 text-sm border-b border-[#E5DFD2]">
          <div>
            <p className="text-[#6B6B5E]">Invoice No.</p>
            <p className="font-mono font-semibold" data-testid="public-invoice-no">{inv.invoice_no}</p>
          </div>
          <div className="text-right">
            <p className="text-[#6B6B5E]">Date</p>
            <p className="font-medium">{dateTime || "—"}</p>
          </div>
          <div>
            <p className="text-[#6B6B5E]">Customer</p>
            <p className="font-medium">{inv.customer_name || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-[#6B6B5E]">Mobile</p>
            <p className="font-mono">{inv.customer_mobile_masked || "—"}</p>
          </div>
        </div>

        {/* Items */}
        <div className="px-8 py-5">
          {inv.freedom_packages?.map((fs, i) => (
            <div key={i} className="mb-4 rounded-md border border-[#C5A059]/50 bg-[#FBF7EE] px-4 py-3" data-testid={`public-invoice-freedom-${i}`}>
              <p className="text-sm font-semibold text-[#15241C]">Freedom Package · Any {fs.count}</p>
              <p className="text-xs text-[#6B6B5E] mt-0.5">{fs.names?.join(", ")}</p>
              <div className="flex justify-between items-baseline mt-2 text-sm">
                <span className="text-[#6B6B5E]">Actual <span className="line-through">{rupee(fs.actual)}</span> → Paid <span className="font-semibold text-[#15241C]">{rupee(fs.paid)}</span></span>
                <span className="font-semibold text-[#2E7D32]">You saved {rupee(fs.saved)}</span>
              </div>
            </div>
          ))}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#6B6B5E] border-b border-[#E5DFD2]">
                <th className="pb-2 font-medium">Service / Product</th>
                <th className="pb-2 font-medium">Staff</th>
                <th className="pb-2 font-medium text-center">Qty</th>
                <th className="pb-2 font-medium text-right">Price</th>
                <th className="pb-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((it, i) => (
                <tr key={i} className="border-b border-[#F0ECE2]" data-testid={`public-invoice-item-${i}`}>
                  <td className="py-2">{it.name}</td>
                  <td className="py-2 text-[#6B6B5E]">{it.staff_name || "—"}</td>
                  <td className="py-2 text-center">{it.qty}</td>
                  <td className="py-2 text-right">{rupee(it.price)}</td>
                  <td className="py-2 text-right font-medium">{rupee(it.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-8 pb-5">
          <div className="ml-auto max-w-xs text-sm space-y-1.5">
            <Row label="Subtotal" value={rupee(inv.subtotal)} />
            {inv.item_discount_total > 0 && <Row label="Service Discounts" value={`- ${rupee(inv.item_discount_total)}`} />}
            {inv.referral_discount > 0 && <Row label="Referral Discount (10%)" value={`- ${rupee(inv.referral_discount)}`} testid="public-invoice-referral-discount" />}
            {inv.discount - (inv.referral_discount || 0) > 0 && <Row label="Other Discount" value={`- ${rupee(inv.discount - (inv.referral_discount || 0))}`} />}
            {inv.tax > 0 && <Row label="Taxable Value" value={rupee(inv.taxable_value)} />}
            {inv.tax > 0 && <Row label={`GST (${Math.round(inv.tax_percent)}% incl.)`} value={rupee(inv.tax)} />}
            <div className="flex justify-between pt-2 mt-1 border-t border-[#C5A059] text-lg font-serif text-[#15241C]" data-testid="public-invoice-total">
              <span>Final Amount Payable</span>
              <span>{rupee(inv.grand_total)}</span>
            </div>
          </div>
        </div>

        {/* Payments */}
        {(inv.payments?.length > 0 || inv.paid_amount > 0) && (
          <div className="px-8 pb-6 text-sm">
            <div className="bg-[#F5F0E6] rounded-md px-4 py-3 max-w-xs ml-auto space-y-1">
              <div className="flex justify-between pb-1 mb-1 border-b border-[#DDD5C4]" data-testid="public-invoice-mode">
                <span className="text-[#7A6E52]">Mode of Payment</span>
                <span className="font-medium">{(inv.payments?.length || 0) > 1 ? "Split Payment" : (inv.payments?.[0]?.method || "—")}</span>
              </div>
              {inv.payments?.map((p, i) => (
                <Row key={i} label={p.method} value={rupee(p.amount)} testid={`public-invoice-payment-${i}`} />
              ))}
              {inv.wallet_used > 0 && <Row label="Wallet Used" value={`- ${rupee(inv.wallet_used)}`} testid="public-invoice-wallet-used" />}
              <div className="flex justify-between pt-1 border-t border-[#DDD5C4] font-medium">
                <span>Actual Payment Received</span>
                <span data-testid="public-invoice-paid">{rupee(inv.cash_received ?? inv.paid_amount)}</span>
              </div>
            </div>
          </div>
        )}

        {inv.referral_code && (
          <div className="px-6 py-4 border-t border-[#DDD5C4] bg-[#FBF7EE]" data-testid="public-invoice-referral">
            <p className="text-sm font-medium">Referral Code: <span className="font-mono text-[#B71C2B]" data-testid="public-invoice-referral-code">{inv.referral_code}</span></p>
            <p className="text-xs text-[#5B5B5B] mt-1">Referral Benefit: {inv.referral_benefit}</p>
          </div>
        )}
        <div className="bg-[#15241C] text-center py-4 text-xs text-white/60">
          Thank you for choosing DEFINE SALONS.
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between text-[#3A3A30]" data-testid={testid}>
    <span className="text-[#6B6B5E]">{label}</span>
    <span>{value}</span>
  </div>
);
